import React, { useEffect, useRef, useState } from 'react';
import {
  Download,
  Eye,
  FileText,
  Loader2,
  Paperclip,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ConfirmDialog from '../ConfirmDialog';
import {
  ATTACHMENT_ACCEPT,
  deleteAttachment,
  formatFileSize,
  getAttachmentName,
  getAttachmentSize,
  isSafeInlinePreview,
  listTaskAttachments,
  openAccessUrl,
  requestAttachmentAccess,
  uploadTaskAttachment,
  validateAttachmentFile,
} from '../../services/attachments';

const getAttachmentStatus = (attachment) => (
  attachment?.status || attachment?.upload_status || 'ready'
);

const isReady = (attachment) => ['ready', 'available', 'completed', 'complete'].includes(getAttachmentStatus(attachment));

const AttachmentSection = ({ api, taskId, user, canUpload }) => {
  const [attachments, setAttachments] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [accessingId, setAccessingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const controllersRef = useRef(new Map());

  const fetchAttachments = async ({ quiet = false } = {}) => {
    if (!taskId) return;
    if (!quiet) setLoading(true);
    try {
      const result = await listTaskAttachments(api, taskId);
      setAttachments(result.attachments);
      setPermissions(result.permissions);
      setUnavailable(false);
    } catch (error) {
      if ([404, 503].includes(error.response?.status)) {
        setUnavailable(true);
        setPermissions({});
      } else {
        toast.error(error.response?.data?.message || 'Failed to load attachments');
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    const uploadControllers = controllersRef.current;
    setAttachments([]);
    setPermissions({});
    setUnavailable(false);
    setUploads([]);
    fetchAttachments();
    return () => {
      uploadControllers.forEach(controller => controller.abort());
      uploadControllers.clear();
    };
    // The API instance is supplied by AuthContext and is intentionally excluded;
    // it is recreated during provider renders while the authenticated session is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const uploadAllowed = typeof canUpload === 'boolean' ? canUpload : permissions.can_upload === true;

  if (unavailable) return null;

  const updateUpload = (uploadId, changes) => {
    setUploads(current => current.map(upload => (
      upload.id === uploadId ? { ...upload, ...changes } : upload
    )));
  };

  const runUpload = async (uploadId, file) => {
    const controller = new AbortController();
    controllersRef.current.set(uploadId, controller);
    updateUpload(uploadId, { status: 'uploading', progress: 0, error: null });
    try {
      await uploadTaskAttachment({
        api,
        taskId,
        file,
        signal: controller.signal,
        onProgress: progress => updateUpload(uploadId, { progress }),
      });
      updateUpload(uploadId, { status: 'complete', progress: 100 });
      await fetchAttachments({ quiet: true });
      setUploads(current => current.filter(upload => upload.id !== uploadId));
      toast.success(`${file.name} uploaded`);
    } catch (error) {
      const cancelled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted;
      updateUpload(uploadId, {
        status: cancelled ? 'cancelled' : 'error',
        error: cancelled ? 'Upload cancelled' : (error.response?.data?.message || error.message || 'Upload failed'),
      });
      if (!cancelled) toast.error(`${file.name}: ${error.response?.data?.message || error.message || 'Upload failed'}`);
    } finally {
      controllersRef.current.delete(uploadId);
    }
  };

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);
    files.forEach((file, index) => {
      const validationError = validateAttachmentFile(file);
      if (validationError) {
        toast.error(`${file.name}: ${validationError}`);
        return;
      }

      const uploadId = `${Date.now()}-${index}-${file.name}`;
      setUploads(current => [...current, {
        id: uploadId,
        file,
        progress: 0,
        status: 'queued',
        error: null,
      }]);
      setTimeout(() => runUpload(uploadId, file), 0);
    });
    event.target.value = '';
  };

  const cancelUpload = (uploadId) => {
    controllersRef.current.get(uploadId)?.abort();
  };

  const retryUpload = (upload) => runUpload(upload.id, upload.file);

  const handleAccess = async (attachment, disposition) => {
    const attachmentId = attachment.id || attachment._id;
    setAccessingId(`${attachmentId}:${disposition}`);
    try {
      const access = await requestAttachmentAccess(api, attachmentId, disposition);
      openAccessUrl(access, attachment, disposition);
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${disposition === 'inline' ? 'preview' : 'download'} attachment`);
    } finally {
      setAccessingId(null);
    }
  };

  const canDeleteAttachment = (attachment) => {
    if (typeof attachment?.permissions?.can_delete === 'boolean') return attachment.permissions.can_delete;
    if (typeof attachment?.can_delete === 'boolean') return attachment.can_delete;
    const uploaderId = attachment?.uploaded_by?.id || attachment?.uploaded_by;
    return user?.role === 'admin' || (!!uploaderId && uploaderId === user?.id);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAttachment(api, deleteTarget.id || deleteTarget._id);
      setAttachments(current => current.filter(item => (item.id || item._id) !== (deleteTarget.id || deleteTarget._id)));
      toast.success('Attachment removed');
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove attachment');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="border-border" data-testid="attachments-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            ATTACHMENTS ({attachments.length})
          </CardTitle>
          {uploadAllowed && (
            <label className="inline-flex items-center justify-center h-8 px-3 text-xs border border-input bg-background hover:bg-accent hover:text-accent-foreground cursor-pointer">
              <Upload className="h-3 w-3 mr-1" />
              UPLOAD
              <Input
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={handleFiles}
                className="sr-only"
                data-testid="attachment-upload-input"
              />
            </label>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {uploads.map(upload => (
          <div key={upload.id} className="p-3 border border-border space-y-2" data-testid={`attachment-upload-${upload.id}`}>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{upload.file.name}</p>
                <p className={`text-[10px] ${upload.status === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {upload.error || `${formatFileSize(upload.file.size)} · ${upload.progress}%`}
                </p>
              </div>
              {['queued', 'uploading'].includes(upload.status) ? (
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => cancelUpload(upload.id)} aria-label={`Cancel ${upload.file.name}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => retryUpload(upload)} aria-label={`Retry ${upload.file.name}`}>
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="h-1.5 bg-muted overflow-hidden" role="progressbar" aria-valuenow={upload.progress} aria-valuemin="0" aria-valuemax="100">
              <div className={`h-full transition-all ${upload.status === 'error' ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${upload.progress}%` }} />
            </div>
          </div>
        ))}

        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : attachments.length > 0 ? (
          <div className="space-y-2">
            {attachments.map(attachment => {
              const attachmentId = attachment.id || attachment._id;
              const status = getAttachmentStatus(attachment);
              const ready = isReady(attachment);
              const downloadAllowed = ready && attachment?.permissions?.can_download !== false;
              return (
                <div key={attachmentId} className="flex items-center gap-2 p-3 border border-border" data-testid={`attachment-${attachmentId}`}>
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate" title={getAttachmentName(attachment)}>{getAttachmentName(attachment)}</p>
                    <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                      <span>{formatFileSize(getAttachmentSize(attachment))}</span>
                      {attachment.uploaded_by_name && <span>· {attachment.uploaded_by_name}</span>}
                      {!ready && <Badge variant={status === 'rejected' ? 'destructive' : 'outline'} className="text-[9px] px-1 py-0">{status.replace(/_/g, ' ').toUpperCase()}</Badge>}
                    </div>
                  </div>
                  {downloadAllowed && isSafeInlinePreview(attachment) && (
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleAccess(attachment, 'inline')} disabled={accessingId === `${attachmentId}:inline`} aria-label={`Preview ${getAttachmentName(attachment)}`}>
                      {accessingId === `${attachmentId}:inline` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  {downloadAllowed && (
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleAccess(attachment, 'attachment')} disabled={accessingId === `${attachmentId}:attachment`} aria-label={`Download ${getAttachmentName(attachment)}`}>
                      {accessingId === `${attachmentId}:attachment` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  {canDeleteAttachment(attachment) && (
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteTarget(attachment)} aria-label={`Remove ${getAttachmentName(attachment)}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-5">
            <Paperclip className="h-6 w-6 text-muted-foreground/40 mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">No attachments yet</p>
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Remove attachment?"
        description={deleteTarget ? `${getAttachmentName(deleteTarget)} will be permanently removed.` : ''}
        confirmLabel="REMOVE"
        onConfirm={handleDelete}
        loading={deleting}
      />
    </Card>
  );
};

export default AttachmentSection;
